/**
 * Web addresses as people type them - "yahoo.com", "www.yahoo.com/about", "https://yahoo.com" - tidied into one
 * that works as a link: https:// added when there is no scheme (http:// for localhost or an IP address, which are
 * development setups). The server tidies the same way (WebAddress.java), so a value saved any way looks alike.
 *
 * @returns {{ value: string, error: string|null }} the tidied address ('' when blank), or the value as typed and a
 *   message saying what is wrong with it
 */
export function normalizeWebAddress(raw) {
  const typed = (raw || '').trim();
  if (!typed) return { value: '', error: null };
  if (/\s/.test(typed)) return { value: typed, error: 'A web address has no spaces, like example.com' };

  const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(typed);
  const bare = typed.replace(/^\/+/, '');
  const local = /^(localhost|\d{1,3}(\.\d{1,3}){3})(:\d+)?([/?#]|$)/i.test(bare);
  let url;
  try {
    url = new URL(hasScheme ? typed : `${local ? 'http' : 'https'}://${bare}`);
  } catch (e) {
    return { value: typed, error: 'Enter a web address like example.com' };
  }
  if (url.protocol !== 'http:' && url.protocol !== 'https:') {
    return { value: typed, error: 'Use a web address like example.com' };
  }
  const host = url.hostname;
  const looksLikeHost = host === 'localhost' || /^\d{1,3}(\.\d{1,3}){3}$/.test(host) || /^[^.]+(\.[^.]+)*\.[a-z][a-z0-9-]+$/i.test(host);
  if (!looksLikeHost) return { value: typed, error: 'Enter a web address like example.com' };

  // URL adds a "/" to a bare address; leave it off unless it was typed
  let value = url.href;
  if (url.pathname === '/' && !url.search && !url.hash && !/\/$/.test(typed)) value = value.slice(0, -1);
  return { value, error: null };
}

/** For showing an address as text: without the https:// and a trailing slash. */
export const displayWebAddress = (value) => (value || '').replace(/^https?:\/\//i, '').replace(/\/$/, '');
